import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Save, Plus, Trash2, Calculator } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { invoicesService, CreateInvoiceDto, InvoiceItem } from '@/services/invoices.service';
import { ventasService } from '@/services/ventas.service';
import { dialogs } from '@/stores/dialog-store';

const TAX_REGIMES = [
  { code: '601', name: 'General de Ley Personas Morales' },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', name: 'Arrendamiento' },
  { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '616', name: 'Sin obligaciones fiscales' },
  { code: '626', name: 'Régimen Simplificado de Confianza' },
];

const CFDI_USES = [
  { code: 'G01', name: 'Adquisición de mercancias' },
  { code: 'G03', name: 'Gastos en general' },
  { code: 'I04', name: 'Equipo de computo y accesorios' },
  { code: 'P01', name: 'Por definir' },
  { code: 'S01', name: 'Sin efectos fiscales' },
];

const PAYMENT_METHODS = [
  { code: 'PUE', name: 'Pago en una sola exhibición' },
  { code: 'PPD', name: 'Pago en parcialidades o diferido' },
];

const PAYMENT_FORMS = [
  { code: '01', name: 'Efectivo' },
  { code: '02', name: 'Cheque nominativo' },
  { code: '03', name: 'Transferencia electrónica de fondos' },
  { code: '04', name: 'Tarjeta de crédito' },
  { code: '28', name: 'Tarjeta de débito' },
  { code: '99', name: 'Por definir' },
];

export function InvoiceFormPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [loading, setLoading] = useState(false);
  const [ticketFolio, setTicketFolio] = useState('');
  
  // Form State
  const [saleId, setSaleId] = useState<string | undefined>(undefined);
  const [customerName, setCustomerName] = useState('');
  const [customerRfc, setCustomerRfc] = useState('');
  const [taxRegime, setTaxRegime] = useState('616');
  const [taxZipCode, setTaxZipCode] = useState('');
  const [cfdiUse, setCfdiUse] = useState('S01');
  const [paymentForm, setPaymentForm] = useState('01');
  const [paymentMethod, setPaymentMethod] = useState('PUE');
  const [email, setEmail] = useState('');
  
  const [items, setItems] = useState<InvoiceItem[]>([]);

  const handleSearchTicket = async () => {
    if (!ticketFolio) return;
    setLoading(true);
    try {
      // 1. Buscar la venta por su folio en la base de datos de la empresa
      const res = await ventasService.findAll({
        companyId: user!.company.id,
        search: ticketFolio,
        limit: 1,
      });

      if (!res.data || res.data.length === 0) {
        dialogs.alert(`No se encontró ningún ticket con el folio "${ticketFolio}".`);
        setLoading(false);
        return;
      }

      const foundSale = res.data[0];

      // 2. Obtener el detalle completo de la venta para traer los items detallados y el cliente
      const fullSale = await ventasService.findById(foundSale.id);
      setSaleId(fullSale.id);

      // 3. Autocompletar datos del cliente si tiene uno asociado
      if (fullSale.customer) {
        setCustomerName(fullSale.customer.name.toUpperCase());
        setCustomerRfc(fullSale.customer.rfc?.toUpperCase() || 'XAXX010101000');
        setTaxZipCode(fullSale.customer.address?.match(/\b\d{5}\b/)?.[0] || '06470');
        setEmail(fullSale.customer.email || '');
        
        // Mapear régimen si existe o valor por defecto
        if (fullSale.customer.taxRegime) {
          setTaxRegime(fullSale.customer.taxRegime);
        }
      } else {
        // Por defecto público en general
        setCustomerName('PÚBLICO EN GENERAL');
        setCustomerRfc('XAXX010101000');
        setTaxZipCode('06470');
        setTaxRegime('616');
        setEmail('');
      }

      // Mapear método y forma de pago según lo registrado en la venta
      if (fullSale.paymentMethod) {
        // Mapear métodos comunes
        const methodMap: Record<string, string> = {
          CASH: '01',
          CARD: '04',
          TRANSFER: '03',
          CREDIT: '99',
        };
        setPaymentForm(methodMap[fullSale.paymentMethod] || '01');
      }

      // 4. Mapear los items de la venta real a conceptos de factura
      const mappedItems = fullSale.items.map((item: any) => {
        const itemSubtotal = Number(item.quantity) * Number(item.unitPrice) - Number(item.discount || 0);
        const taxRate = 0.16;
        const taxAmount = itemSubtotal * taxRate;
        const total = itemSubtotal + taxAmount;

        return {
          productId: item.productId,
          satProductKey: item.product?.satKey || '50192100', // Código genérico de abarrotes o el configurado
          satUnitKey: item.product?.unit?.satUnitKey || 'H87', // Clave Unidad SAT configurada o pieza
          description: item.product?.name || 'Concepto de venta',
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice),
          discount: Number(item.discount || 0),
          taxBase: itemSubtotal,
          taxRate,
          taxAmount,
          subtotal: itemSubtotal,
          total,
        };
      });

      setItems(mappedItems);
      dialogs.alert(`Ticket ${fullSale.folio} cargado correctamente.`);
    } catch (err: any) {
      dialogs.alert('Error al buscar el ticket. Revisa el folio e intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        satProductKey: '50192100',
        satUnitKey: 'H87',
        description: '',
        quantity: 1,
        unitPrice: 0,
        discount: 0,
        taxBase: 0,
        taxRate: 0.16,
        taxAmount: 0,
        subtotal: 0,
        total: 0,
      }
    ]);
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...items];
    const item = { ...newItems[index], [field]: value };
    
    // Recalculate if numbers change
    if (['quantity', 'unitPrice', 'discount'].includes(field)) {
      item.subtotal = (item.quantity * item.unitPrice) - item.discount;
      item.taxBase = item.subtotal;
      item.taxAmount = item.taxBase * item.taxRate;
      item.total = item.subtotal + item.taxAmount;
    }
    
    newItems[index] = item;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const totals = items.reduce(
    (acc, item) => ({
      subtotal: acc.subtotal + item.subtotal,
      discountTotal: acc.discountTotal + item.discount,
      taxTotal: acc.taxTotal + item.taxAmount,
      grandTotal: acc.grandTotal + item.total,
    }),
    { subtotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const handleSubmit = async () => {
    if (!user?.company?.id || !user?.branch?.id) return;
    
    if (!saleId) {
      dialogs.alert('Por favor busca y carga un folio de ticket de venta antes de emitir la factura.');
      return;
    }

    if (!customerName || !customerRfc || !taxZipCode) {
      dialogs.alert('Por favor completa todos los datos del receptor (Nombre, RFC y Código Postal).');
      return;
    }
    
    if (items.length === 0) {
      dialogs.alert('La factura debe tener al menos un concepto.');
      return;
    }
    
    setLoading(true);
    try {
      const dto: CreateInvoiceDto = {
        saleId,
        customerName,
        customerRfc,
        taxRegime,
        taxZipCode,
        cfdiUse,
        paymentForm,
        paymentMethod,
        email: email || undefined,
      };
      
      await invoicesService.create(user.company.id, user.branch.id, dto);
      await dialogs.alert('Factura timbrada exitosamente.');
      navigate('/invoices');
    } catch (error: any) {
      const errMessage = error.response?.data?.message || error.message || 'Error al timbrar la factura. Revisa los datos.';
      dialogs.alert(errMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full pb-20">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-ios-gray-6 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Nueva Factura (CFDI 4.0)</h1>
          <p className="text-sm text-ios-gray mt-1">Completa los datos fiscales para emitir el comprobante</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Panel Origen */}
        <div className="ios-card p-6">
          <h2 className="text-lg font-semibold mb-4 border-b border-ios-gray-5 pb-2">1. Origen de la Factura</h2>
          <div className="flex items-end gap-4 max-w-md">
            <div className="flex-1">
              <label className="text-sm font-medium text-ios-gray mb-1 block">Facturar Ticket (Opcional)</label>
              <input
                type="text"
                value={ticketFolio}
                onChange={(e) => setTicketFolio(e.target.value)}
                placeholder="Ej. TKT-00123"
                className="ios-input w-full"
              />
            </div>
            <button
              onClick={handleSearchTicket}
              className="ios-button-outline px-4 py-2 flex items-center gap-2"
            >
              <Search size={18} /> Buscar
            </button>
          </div>
          <p className="text-xs text-ios-gray mt-2">Si no ingresas un ticket, podrás facturar de forma manual (Factura Libre).</p>
        </div>

        {/* Panel Receptor */}
        <div className="ios-card p-6">
          <h2 className="text-lg font-semibold mb-4 border-b border-ios-gray-5 pb-2">2. Datos del Receptor</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">RFC *</label>
              <input
                type="text"
                value={customerRfc}
                onChange={(e) => setCustomerRfc(e.target.value.toUpperCase())}
                placeholder="XAXX010101000"
                className="ios-input w-full uppercase font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Nombre o Razón Social *</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value.toUpperCase())}
                placeholder="PÚBLICO EN GENERAL"
                className="ios-input w-full uppercase"
              />
              <p className="text-[10px] text-orange-500 mt-1">Debe estar exactamente igual que en la Constancia de Situación Fiscal (Sin SA de CV).</p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Código Postal (Domicilio Fiscal) *</label>
              <input
                type="text"
                value={taxZipCode}
                onChange={(e) => setTaxZipCode(e.target.value)}
                placeholder="00000"
                maxLength={5}
                className="ios-input w-full"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Régimen Fiscal *</label>
              <select
                value={taxRegime}
                onChange={(e) => setTaxRegime(e.target.value)}
                className="ios-input w-full"
              >
                {TAX_REGIMES.map(r => (
                  <option key={r.code} value={r.code}>{r.code} - {r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Correo Electrónico (Para enviar XML/PDF)</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cliente@correo.com"
                className="ios-input w-full"
              />
            </div>
          </div>
        </div>

        {/* Panel Pago y Uso */}
        <div className="ios-card p-6">
          <h2 className="text-lg font-semibold mb-4 border-b border-ios-gray-5 pb-2">3. Uso de CFDI y Pago</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Uso de CFDI *</label>
              <select
                value={cfdiUse}
                onChange={(e) => setCfdiUse(e.target.value)}
                className="ios-input w-full"
              >
                {CFDI_USES.map(u => (
                  <option key={u.code} value={u.code}>{u.code} - {u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Método de Pago *</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="ios-input w-full"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m.code} value={m.code}>{m.code} - {m.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-ios-gray mb-1 block">Forma de Pago *</label>
              <select
                value={paymentForm}
                onChange={(e) => setPaymentForm(e.target.value)}
                className="ios-input w-full"
              >
                {PAYMENT_FORMS.map(f => (
                  <option key={f.code} value={f.code}>{f.code} - {f.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Conceptos */}
        <div className="ios-card p-6">
          <div className="flex items-center justify-between mb-4 border-b border-ios-gray-5 pb-2">
            <h2 className="text-lg font-semibold">4. Conceptos</h2>
            <button
              onClick={addItem}
              className="text-ios-blue text-sm font-medium flex items-center gap-1 hover:underline"
            >
              <Plus size={16} /> Añadir Concepto
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b border-ios-gray-5 text-ios-gray text-xs uppercase tracking-wider text-left">
                  <th className="py-2 px-2 w-32">Clave SAT</th>
                  <th className="py-2 px-2 w-24">Unidad</th>
                  <th className="py-2 px-2">Descripción</th>
                  <th className="py-2 px-2 w-20">Cant.</th>
                  <th className="py-2 px-2 w-28">Precio U.</th>
                  <th className="py-2 px-2 w-28">Impuesto</th>
                  <th className="py-2 px-2 w-28 text-right">Importe</th>
                  <th className="py-2 px-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-6">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-ios-gray">
                      No hay conceptos agregados.
                    </td>
                  </tr>
                ) : (
                  items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="py-2 px-2">
                        <input type="text" value={item.satProductKey} onChange={(e) => updateItem(idx, 'satProductKey', e.target.value)} className="w-full p-1 border border-ios-gray-5 rounded text-xs font-mono" placeholder="01010101" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="text" value={item.satUnitKey} onChange={(e) => updateItem(idx, 'satUnitKey', e.target.value)} className="w-full p-1 border border-ios-gray-5 rounded text-xs font-mono" placeholder="H87" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="text" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} className="w-full p-1 border border-ios-gray-5 rounded text-xs" placeholder="Descripción del producto" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" min="1" value={item.quantity || ''} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} className="w-full p-1 border border-ios-gray-5 rounded text-xs" />
                      </td>
                      <td className="py-2 px-2">
                        <input type="number" min="0" value={item.unitPrice || ''} onChange={(e) => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-full p-1 border border-ios-gray-5 rounded text-xs" />
                      </td>
                      <td className="py-2 px-2">
                        <div className="text-xs text-ios-gray flex flex-col">
                          <span>IVA 16%</span>
                          <span>${item.taxAmount.toFixed(2)}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right font-medium">
                        ${item.total.toFixed(2)}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <button onClick={() => removeItem(idx)} className="text-ios-gray hover:text-ios-red">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="mt-6 flex justify-end">
            <div className="w-72 bg-ios-gray-6/30 p-4 rounded-xl space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-ios-gray">Subtotal:</span>
                <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ios-gray">Descuento:</span>
                <span className="font-medium">${totals.discountTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-ios-gray">Impuestos (IVA 16%):</span>
                <span className="font-medium">${totals.taxTotal.toFixed(2)}</span>
              </div>
              <div className="border-t border-ios-gray-4 pt-2 flex justify-between text-lg font-bold">
                <span>Total:</span>
                <span>${totals.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button
            onClick={() => navigate(-1)}
            className="ios-button-outline px-6"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="ios-button px-8 flex items-center gap-2 text-lg"
          >
            {loading ? 'Timbrando...' : (
              <>
                <Calculator size={20} /> Timbrar Factura
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
